/**
 * Wraps the PWA install flow around the (Chromium-only) `beforeinstallprompt` event. The browser
 * fires that event when the app meets the install criteria; per the platform contract we must call
 * `preventDefault()` and stash it, then later call `prompt()` from a user gesture to show the install
 * dialog. This module captures the event as early as possible (the event can fire before any
 * component mounts) and lets UI subscribe to availability changes and trigger the prompt.
 *
 * See https://developer.mozilla.org/en-US/docs/Web/Progressive_web_apps/Guides/Making_PWAs_installable
 */

/** The non-standard event exposed by Chromium browsers; typed structurally to avoid lib dependence. */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

type AvailabilityListener = (available: boolean) => void;

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let installed = false;
let initialized = false;
let onBeforeInstallPrompt: ((event: Event) => void) | null = null;
let onAppInstalled: (() => void) | null = null;
const listeners = new Set<AvailabilityListener>();

function notify(): void {
  const available = canInstall();
  for (const listener of listeners) {
    listener(available);
  }
}

/** Whether the install prompt is currently available to show. */
export function canInstall(): boolean {
  return deferredPrompt != null && !installed;
}

/** Begins listening for the install events. Safe to call multiple times (idempotent). */
export function initInstallPrompt(): void {
  if (initialized || typeof window === 'undefined') {
    return;
  }
  initialized = true;
  onBeforeInstallPrompt = (event: Event) => {
    // Stash the event so we can trigger the prompt later from a user gesture.
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    notify();
  };
  onAppInstalled = () => {
    installed = true;
    deferredPrompt = null;
    notify();
  };
  window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
  window.addEventListener('appinstalled', onAppInstalled);
}

/** Subscribes to install-availability changes; returns an unsubscribe function. */
export function onInstallAvailabilityChange(listener: AvailabilityListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Shows the browser's install dialog (must be called from a user gesture). Resolves with the user's
 * choice, or `'unavailable'` if no install prompt is currently pending. The stashed event is single-use,
 * so it's cleared afterward.
 */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const event = deferredPrompt;
  if (!event) {
    return 'unavailable';
  }
  await event.prompt();
  const choice = await event.userChoice;
  deferredPrompt = null;
  notify();
  return choice.outcome;
}

/** Test-only: resets module state (and removes the window listeners) so each test starts clean. */
export function _resetInstallPromptForTests(): void {
  if (typeof window !== 'undefined') {
    if (onBeforeInstallPrompt) {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    }
    if (onAppInstalled) {
      window.removeEventListener('appinstalled', onAppInstalled);
    }
  }
  onBeforeInstallPrompt = null;
  onAppInstalled = null;
  deferredPrompt = null;
  installed = false;
  initialized = false;
  listeners.clear();
}
